import { useReinsurerShare } from "hooks/useRIFacPremium";
import { CurrencyTotal, DEFAULT_COLORS } from "interfaces/currency";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
} from "recharts";
import { Loader, AlertCircle, PieChart as PieChartIcon } from "lucide-react";
import { convertCurrency } from "utils/helpers";

const ShareComponent: React.FC = () => {
    const { t } = useTranslation();
    const { reinsurerShare, loading, error } = useReinsurerShare();

    const { currencyTotals, totalShare, pieChartData } = useMemo(() => {
        if (!reinsurerShare || reinsurerShare.length === 0) {
            return {
                currencyTotals: [],
                totalShare: 0,
                pieChartData: [],
            };
        }

        const currencyMap: Record<string, number> = {};

        reinsurerShare.forEach((item) => {
            if (item?.currency && item?.total_facultative_premium !== null) {
                const currency = convertCurrency(item.currency);
                currencyMap[currency] =
                    (currencyMap[currency] || 0) +
                    item.total_facultative_premium;
            }
        });

        const currencyTotals: CurrencyTotal[] = Object.entries(currencyMap)
            .filter(([_, amount]) => amount > 0)
            .map(([currency, amount], index) => ({
                currency,
                amount,
                color: DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                percentage: 0, // Will be calculated after total is known
            }))
            .sort((a, b) => b.amount - a.amount);

        const totalShare = currencyTotals.reduce(
            (total, item) => total + item.amount,
            0,
        );

        // Calculate percentages
        currencyTotals.forEach((item) => {
            item.percentage =
                totalShare > 0 ? (item.amount / totalShare) * 100 : 0;
        });

        const pieChartData = currencyTotals.map((item) => ({
            name: item.currency,
            value: item.amount,
            percentage: item.percentage,
        }));

        return {
            currencyTotals,
            totalShare,
            pieChartData,
        };
    }, [reinsurerShare]);

    const formatCurrency = (amount: number, currency: string): string => {
        try {
            return new Intl.NumberFormat(undefined, {
                style: "currency",
                currency: currency,
                currencyDisplay: "code",
                minimumFractionDigits: 0,
                maximumFractionDigits: amount >= 1000000 ? 1 : 0,
            }).format(amount);
        } catch {
            return `${amount.toLocaleString()} ${currency}`;
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center text-gray-500">
                        <Loader className="animate-spin h-8 w-8 text-green-500 mb-3" />
                        <span className="text-sm font-medium">
                            {t("dashboard.share.loading")}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center text-red-500">
                        <AlertCircle className="h-8 w-8 mb-3" />
                        <span className="text-sm font-medium">
                            {t("dashboard.share.error")}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-xs p-6 h-full flex flex-col">
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">
                        {t("dashboard.share.title")}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {t("dashboard.share.description")}
                    </p>
                </div>
                {currencyTotals.length > 0 && (
                    <div className="bg-green-50 rounded-lg px-3 py-2">
                        <span className="text-xs font-medium text-green-700">
                            {t("dashboard.share.total_share")}
                        </span>
                        <p className="text-lg font-semibold text-green-900">
                            {formatCurrency(
                                totalShare,
                                currencyTotals[0]?.currency || "USD",
                            )}
                        </p>
                    </div>
                )}
            </div>

            {currencyTotals.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                        <div className="space-y-4">
                            <h3 className="text-sm font-medium text-gray-700">
                                {t("dashboard.share.currency_distribution")}
                            </h3>
                            <div className="space-y-3">
                                {currencyTotals.map((item) => (
                                    <div
                                        key={item.currency}
                                        className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div
                                                className="w-3 h-3 rounded-full mr-3"
                                                style={{
                                                    backgroundColor: item.color,
                                                }}
                                            />
                                            <span className="font-medium text-gray-900">
                                                {item.currency}
                                            </span>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-medium">
                                                {formatCurrency(
                                                    item.amount,
                                                    item.currency,
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {item.percentage.toFixed(1)}%
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieChartData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="value"
                                        label={({ name, percentage }) =>
                                            `${name} ${percentage.toFixed(1)}%`
                                        }
                                        labelLine={false}>
                                        {pieChartData.map((_, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={
                                                    DEFAULT_COLORS[
                                                        index %
                                                            DEFAULT_COLORS.length
                                                    ]
                                                }
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(
                                            value: number,
                                            name: string,
                                            entry: any,
                                        ) => [
                                            formatCurrency(value, name),
                                            `${entry.payload.percentage.toFixed(1)}% of total`,
                                        ]}
                                    />
                                    <Legend
                                        formatter={(value) => (
                                            <span className="text-xs text-gray-600">
                                                {value}
                                            </span>
                                        )}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                        <h3 className="text-sm font-medium text-gray-700 mb-4">
                            {t("dashboard.share.detailed_breakdown")}
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {t("dashboard.share.currency")}
                                        </th>
                                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {t("dashboard.share.amount")}
                                        </th>
                                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                            {t("dashboard.share.allocation")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currencyTotals.map((item, index) => (
                                        <tr
                                            key={item.currency}
                                            className={`hover:bg-gray-50 transition-colors ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"}`}>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center">
                                                    <div
                                                        className="w-4 h-4 rounded-full mr-3"
                                                        style={{
                                                            backgroundColor:
                                                                item.color,
                                                        }}
                                                    />
                                                    <span className="font-medium text-gray-900">
                                                        {item.currency}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-right font-medium">
                                                {formatCurrency(
                                                    item.amount,
                                                    item.currency,
                                                )}
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center justify-end">
                                                    <div className="w-full max-w-[120px]">
                                                        <div className="flex items-center">
                                                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                                                                <div
                                                                    className="h-2 rounded-full"
                                                                    style={{
                                                                        width: `${item.percentage}%`,
                                                                        backgroundColor:
                                                                            item.color,
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="text-xs text-gray-500 ml-2 w-8 text-right">
                                                                {item.percentage.toFixed(
                                                                    1,
                                                                )}
                                                                %
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                    <PieChartIcon className="h-10 w-10 mb-3" />
                    <p className="font-medium">
                        {t("dashboard.share.empty_title")}
                    </p>
                    <p className="text-sm mt-1">
                        {t("dashboard.share.empty_description")}
                    </p>
                </div>
            )}
        </div>
    );
};

export default ShareComponent;
